import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import { DateSelection, IDateSelection } from '@monorepo/controlled/src/stores/date.store';
import { defaultStaticRanges, defaultInputRanges } from '@monorepo/base/src/components/dater';
import { dayDiffBetweenDates } from '@monorepo/tools/src/lib/utils/date';
import { Dater } from '@monorepo/base/src/components/dater/Dater/dater';
import { sendGtagEvent } from '@monorepo/tools/src/lib/tools/tracking';
import { TrackingActions } from '@monorepo/tools/src/lib/consts/tracking/actions';
import { EVENTS } from '@monorepo/tools/src/lib/consts/tracking/events';
import { defineds, todayRange } from '@monorepo/base/src/components/dater/defaultRanges';

const DEFAULT_DAYS = 14;
const STORAGE_KEY = 'bidder-changes-logs-date-selection';

export interface SelectionRanges {
	[key: string]: Range;
}

export interface IDaterOptions {
	label?: string;
}

const defaultMinDate = dayjs()
	.subtract(365 * 4, 'day')
	.toDate();
const defaultMaxDate = new Date(); // Allow selecting up to current time

interface IBidderChangesDater {
	minDate?: Date;
	maxDate?: Date;
	onDateChange?: (dateSelection: IDateSelection) => void;
}

const getDefaultDateSelection = (): IDateSelection => {
	return new DateSelection({
		startDate: defineds.startOfDaysAgo(DEFAULT_DAYS),
		endDate: defineds.startOfToday,
		label: `Last ${DEFAULT_DAYS} Days`,
		key: 'primary',
	});
};

const getStoredDateSelection = (): IDateSelection | null => {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored) {
			const parsed = JSON.parse(stored);
			return new DateSelection({
				startDate: new Date(parsed.startDate),
				endDate: new Date(parsed.endDate),
				label: parsed.label,
				key: 'primary',
			});
		}
	} catch (error) {
		console.warn('Failed to parse stored date selection:', error);
	}
	return null;
};

const storeDateSelection = (dateSelection: IDateSelection) => {
	try {
		localStorage.setItem(
			STORAGE_KEY,
			JSON.stringify({
				startDate: dateSelection.startDate,
				endDate: dateSelection.endDate,
				label: dateSelection.label,
			})
		);
	} catch (error) {
		console.warn('Failed to store date selection:', error);
	}
};

export const BidderChangesDater = observer((props: IBidderChangesDater) => {
	const { minDate = defaultMinDate, maxDate = defaultMaxDate, onDateChange } = props;

	const getInitialDateSelection = () => {
		const stored = getStoredDateSelection();
		return stored || getDefaultDateSelection();
	};

	const [dateSelection, setDateSelection] = useState<IDateSelection>(getInitialDateSelection);
	const [isOpen, setOpen] = useState<boolean>(false);
	const [dateRange, setDateRange] = useState<IDateSelection>(dateSelection);

	const inputDateLabels = defaultInputRanges.map(r => r.label);
	const dayDiff =
		dateSelection.startDate && dateSelection.endDate ? dayDiffBetweenDates(dateSelection.startDate, dateSelection.endDate) : 0;

	let dateLabel = dateSelection.label;
	if (inputDateLabels.includes(dateSelection.label || '') && dayDiff > 0) {
		dateLabel = `${dayDiff} ${dateSelection.label}`;
	}

	const fromDate = dateSelection.startDate ? dayjs(dateSelection.startDate).format('MMM D, YYYY') : '';
	const toDate = dateSelection.endDate ? dayjs(dateSelection.endDate).format('MMM D, YYYY') : '';

	useEffect(() => {
		setDateRange(dateSelection);
	}, [dateSelection]);

	// Reset to default when component mounts (page is visited)
	useEffect(() => {
		const defaultSelection = getDefaultDateSelection();
		setDateSelection(defaultSelection);
		setDateRange(defaultSelection);
		onDateChange?.(defaultSelection);
	}, []);

	const handleSelect = (ranges: SelectionRanges, options: IDaterOptions) => {
		const currentRange = ranges.primary || ranges.initial;
		if (currentRange) {
			setDateRange({
				...currentRange,
				key: 'primary',
				label: options?.label || 'Custom',
			});
		}
	};

	const onApply = () => {
		const newDateSelection = new DateSelection({ ...dateRange, key: 'primary' });

		sendGtagEvent({
			action: TrackingActions.Click,
			category: EVENTS.CLICK.DATER.APPLY,
			label: newDateSelection.label,
			value: `${newDateSelection.startDate.toLocaleDateString()} - ${newDateSelection.endDate.toLocaleDateString()}`,
		});

		setDateSelection(newDateSelection);
		storeDateSelection(newDateSelection);
		onDateChange?.(newDateSelection);
		setOpen(false);
	};

	return (
		<Dater
			dateLabel={dateLabel}
			fromDate={fromDate}
			toDate={toDate}
			isOpen={isOpen}
			setOpen={setOpen}
			dateRange={dateRange}
			handleSelect={handleSelect}
			onApply={onApply}
			minDate={minDate}
			maxDate={maxDate}
			staticRanges={[maxDate === defineds.endOfToday && todayRange, ...defaultStaticRanges].filter(Boolean)}
		/>
	);
});
